import { useEffect, useRef } from "react";
import {
  FaCheck,
  FaCheckCircle,
  FaChevronRight,
  FaCircle,
  FaLock,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

export function InstallationPickerRow({
  icon: Icon,
  iconVariant = "default",
  title,
  subtitle,
  placeholder,
  onClick,
  disabled,
  divided,
  adminOnly = false,
}) {
  const displaySubtitle = subtitle?.trim() ? subtitle : placeholder;

  return (
    <button
      type="button"
      className={
        divided
          ? "installation-settings-cell installation-settings-cell-divided"
          : "installation-settings-cell"
      }
      onClick={onClick}
      disabled={disabled}
    >
      {Icon && (
        <span
          className={`installation-settings-cell-icon installation-settings-cell-icon-${iconVariant}`}
          aria-hidden
        >
          <Icon />
        </span>
      )}
      <span className="installation-settings-cell-body">
        <span className="installation-settings-cell-title">{title}</span>
        {displaySubtitle && (
          <span
            className={
              subtitle?.trim()
                ? "installation-settings-cell-subtitle"
                : "installation-settings-cell-subtitle installation-settings-cell-subtitle-muted"
            }
          >
            {displaySubtitle}
          </span>
        )}
      </span>
      {adminOnly && (
        <FaLock className="installation-settings-cell-lock" aria-hidden />
      )}
      <FaChevronRight
        className="installation-settings-cell-chevron"
        aria-hidden
      />
    </button>
  );
}

function useKeyboardAwareOverlay(open) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return undefined;
    }

    const overlayNode = overlayRef.current;

    const syncOverlayWithViewport = () => {
      const overlay = overlayRef.current;
      if (!overlay) {
        return;
      }
      overlay.style.top = `${viewport.offsetTop}px`;
      overlay.style.height = `${viewport.height}px`;
    };

    syncOverlayWithViewport();
    viewport.addEventListener("resize", syncOverlayWithViewport);
    viewport.addEventListener("scroll", syncOverlayWithViewport);

    return () => {
      viewport.removeEventListener("resize", syncOverlayWithViewport);
      viewport.removeEventListener("scroll", syncOverlayWithViewport);
      if (overlayNode) {
        overlayNode.style.top = "";
        overlayNode.style.height = "";
      }
    };
  }, [open]);

  return overlayRef;
}

export function InstallationPickerSheet({
  open,
  title,
  onClose,
  onDismiss,
  onSave,
  saveLabel,
  dirty = false,
  saving = false,
  onSheetCancel,
  onSheetValidate,
  sheetCancelLabel,
  sheetValidateLabel,
  sheetValidateDisabled = false,
  keyboardAware = false,
  children,
}) {
  const { t } = useTranslation();
  const overlayRef = useKeyboardAwareOverlay(open && keyboardAware);

  if (!open) {
    return null;
  }

  const dismiss = onDismiss || onClose;
  const showSheetFooter = Boolean(onSheetCancel && onSheetValidate);
  const isSaveAction = !showSheetFooter && dirty && onSave;

  const handleHeaderAction = () => {
    if (isSaveAction) {
      onSave();
      return;
    }
    dismiss?.();
  };

  const actionLabel = isSaveAction
    ? saving
      ? t("installation_saving")
      : saveLabel || t("installation_save")
    : t("installation_picker_close");

  const handleOverlayClick = () => {
    if (saving) {
      return;
    }
    if (showSheetFooter) {
      onSheetCancel();
      return;
    }
    dismiss?.();
  };

  return (
    <div
      ref={keyboardAware ? overlayRef : undefined}
      className={
        keyboardAware
          ? "installation-picker-overlay installation-picker-overlay-keyboard-aware"
          : "installation-picker-overlay"
      }
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className={
          showSheetFooter
            ? "installation-picker-sheet installation-picker-sheet-with-footer"
            : "installation-picker-sheet"
        }
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="installation-picker-sheet-header">
          <h4 className="installation-picker-sheet-title">{title}</h4>
          {!showSheetFooter && (
            <button
              type="button"
              className={
                isSaveAction
                  ? "installation-picker-sheet-action installation-picker-sheet-action-save"
                  : "installation-picker-sheet-action"
              }
              onClick={handleHeaderAction}
              disabled={saving}
            >
              {actionLabel}
            </button>
          )}
        </div>
        <div className="installation-picker-sheet-body">{children}</div>
        {showSheetFooter && (
          <div className="installation-picker-sheet-footer photo-capture-wizard-actions photo-capture-wizard-actions-split">
            <button
              type="button"
              className="photo-capture-wizard-secondary"
              onClick={onSheetCancel}
              disabled={saving}
            >
              {sheetCancelLabel || t("mobile_photo_sheet_cancel")}
            </button>
            <button
              type="button"
              className="installation-save-sheet-button photo-capture-wizard-primary"
              onClick={onSheetValidate}
              disabled={saving || sheetValidateDisabled}
            >
              {sheetValidateLabel || t("mobile_photo_sheet_validate")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function InstallationPickerOption({
  label,
  description,
  selected,
  onSelect,
}) {
  return (
    <button
      type="button"
      className={
        selected
          ? "installation-picker-option selected"
          : "installation-picker-option"
      }
      onClick={onSelect}
    >
      <div className="installation-picker-option-text">
        <span className="installation-picker-option-label">{label}</span>
        {description && (
          <span className="installation-picker-option-desc">{description}</span>
        )}
      </div>
      <span className="installation-picker-option-check-slot" aria-hidden>
        <FaCheck
          className={
            selected
              ? "installation-picker-option-check"
              : "installation-picker-option-check installation-picker-option-check-hidden"
          }
        />
      </span>
    </button>
  );
}

export function InstallationPickerCheckboxOption({
  label,
  selected,
  onToggle,
}) {
  return (
    <button
      type="button"
      className={
        selected
          ? "installation-picker-checkbox selected"
          : "installation-picker-checkbox"
      }
      onClick={onToggle}
    >
      <span
        className={
          selected
            ? "installation-picker-checkbox-icon installation-picker-checkbox-icon-on"
            : "installation-picker-checkbox-icon"
        }
        aria-hidden
      >
        {selected ? <FaCheckCircle /> : <FaCircle />}
      </span>
      <span className="installation-picker-checkbox-label">{label}</span>
    </button>
  );
}
